import { BlogCard } from "@/components/blog/BlogCard";
import { BlogNewsletterSignupSection } from "@/components/blog/BlogNewsletterSignupSection";
import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";
import { useBlogList } from "@/hooks/useBlogs";
import { blogCategories, mapDbBlogToPost } from "@/lib/blogs";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";

export function BackstageTalkPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: dbBlogs, loading } = useBlogList({ staticFallback: false });

  useSEO({
    title: "Backstage Talk",
    description:
      "Your backstage pass to South Asian events--expert tips, choreography breakdowns, and cultural insights for unforgettable performances.",
    canonical: "/backstage-talk",
  });

  const blogPosts = useMemo(
    () => (dbBlogs ?? []).map(mapDbBlogToPost),
    [dbBlogs],
  );

  const visiblePosts = useMemo(() => {
    if (!selectedCategory) {
      return blogPosts;
    }

    return blogPosts.filter((post) => post.category === selectedCategory);
  }, [selectedCategory, blogPosts]);

  const [featuredPost, ...gridPosts] = visiblePosts;

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large">
                <div className="text-align-center">
                  <div className="text-[14px] font-semibold tracking-[0.075em] text-pink-dark uppercase">BLOGS</div>
                  <div className="spacer-small" />
                  <h1 className="heading-style-h2 m-0">Backstage talks</h1>
                  <div className="spacer-small" />
                  <p className="align-center mx-auto max-w-[39ch]">
                    Your go-to space for dance tips, performance inspo, and everything in between.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large is-top0">
                <div className="flex flex-col gap-8">
                  <div className="text-[18px] font-medium leading-[27px]">Filter by topic</div>

                  <div className="flex justify-between gap-10 max-[991px]:gap-4 max-[767px]:flex-col max-[767px]:grid-cols-1">
                    {blogCategories.map((category) => {
                      const isActive = selectedCategory === category;

                      return (
                        <button
                          key={category}
                          type="button"
                          className={cn(
                            "relative flex w-full cursor-pointer items-center justify-center overflow-clip rounded-[20px] border border-pink-dark bg-white p-4 px-8 text-neutral-black",
                            isActive && "border-yellow-brand bg-yellow-brand text-white",
                            !isActive && "hover:text-pink-dark",
                          )}
                          onClick={() => setSelectedCategory((current) => (current === category ? null : category))}
                        >
                          <span className="mb-0">{category}</span>
                        </button>
                      );
                    })}
                  </div>

                  {loading ? (
                    <div className="flex flex-col gap-8" aria-label="Loading blog posts">
                      <div className="h-[26rem] animate-pulse rounded-3xl bg-white shadow-[0_24px_55px_rgba(74,25,60,0.07)] max-[767px]:h-[22rem]" />
                      <div className="grid grid-cols-[1fr_1fr_1fr] gap-4 max-[991px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[767px]:grid-cols-1">
                        {Array.from({ length: 6 }).map((_, index) => (
                          <div
                            key={index}
                            className="h-[24rem] animate-pulse rounded-3xl bg-white shadow-[0_24px_55px_rgba(74,25,60,0.07)]"
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      {featuredPost ? <BlogCard post={featuredPost} featured /> : null}

                      {gridPosts.length ? (
                        <div className="grid grid-cols-[1fr_1fr_1fr] gap-4 max-[991px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[767px]:grid-cols-1">
                          {gridPosts.map((post) => (
                            <BlogCard key={post.slug} post={post} />
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <BlogNewsletterSignupSection />
      </main>

      <Footer />
    </div>
  );
}

export default BackstageTalkPage;
